import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { _className, ComponentSync, Grid, LL_V_L } from '@nu-art/thunderstorm-frontend/index';
import { ModuleFE_Advisor_FindingView, ModuleFE_Advisor_MeasurementUnit, ModuleFE_Advisor_Value, ModuleFE_Advisor_Variable } from '@app/advisor-frontend';
import { Component_FindingGroup } from '../Component_FindingGroup/Component_FindingGroup.js';
import './Component_FindingViewSection.scss';
import { findingViewSectionType_ByOrder, findingViewSectionType_FishBone, findingViewSectionType_Flat, findingViewSectionType_Graph } from '@app/advisor-ui-shared';
import { Component_FindingResults } from '../Component_FindingResults/Component_FindingResults.js';
import { translateFishboneResults, translateGraphResults } from './utils.js';
import { Renderer_FishBone, Renderer_SectionGraph } from '@app/advisor-ui-frontend/index';
import { advisorConfig_DevMode, ModuleFE_AdvisorSettings } from '../../../../../modules/ModuleFE_AdvisorSettings/index.js';
import { _values } from '@nu-art/ts-common';
import { valueID_Pos } from '@app/hcs-shared';
import { ModuleFE_AdvisorAnalytics } from '../../../../../_analytics/consts.js';
import { ModuleFE_PanelFindings } from '../../../../../modules/ModuleFE_PanelFindings/ModuleFE_PanelFindings.js';
export class Component_FindingViewSection extends ComponentSync {
    graphRef = React.createRef();
    //######################### Life Cycle #########################
    __onAdvisorBoardTransitionEnd = (columnKey) => {
        if (this.state.section.type !== findingViewSectionType_Graph)
            return;
        if (!this.graphRef.current)
            return;
        this.graphRef.current.setDimensions();
    };
    __onAdvisorSettingsUpdated(key) {
        if (key !== 'hideNegative')
            return;
        this.forceUpdate();
    }
    __onRuntimeFindingsUpdated = (...params) => {
        //Section should re-render to display any new findings
        this.forceUpdate();
    };
    deriveStateFromProps(nextProps, state) {
        state.section = nextProps.section;
        state.hideSectionLabel = !!nextProps.hideSectionLabel;
        state.hideIfEmpty = !!nextProps.hideIfEmpty;
        state.onlyAbnormalResults = !!nextProps.onlyAbnormalResults;
        state.className = nextProps.className;
        return state;
    }
    //######################### Logic #########################
    getSectionClassName = () => {
        const section = this.state.section;
        return _className('finding-view-section', section.type, this.state.className);
    };
    //######################### Render #########################
    render() {
        switch (this.state.section.type) {
            case findingViewSectionType_Flat:
                return this.renderFindings_Flat();
            case findingViewSectionType_ByOrder:
                return this.renderFindings_ByOrder();
            case findingViewSectionType_FishBone:
                return this.renderFindings_FishBone();
            case findingViewSectionType_Graph:
                return this.renderFindings_Graph();
            default:
                return _jsx(_Fragment, {});
        }
    }
    renderFindings_Flat = () => {
        const section = this.state.section;
        const results = ModuleFE_Advisor_FindingView.getFindingsForSection.flat(section, this.state.onlyAbnormalResults);
        if (!results.length && !section.config.alwaysVisible)
            return _jsx(_Fragment, {});
        const gridClassName = _className('findings-grid', section.config.allowFindingEdit && 'allow-edit');
        return _jsxs(LL_V_L, { className: this.getSectionClassName(), children: [!this.state.hideSectionLabel &&
                    _jsx("div", { className: 'finding-view-section__label', children: section.label }), _jsx(Grid, { className: gridClassName, children: results.map(result => _jsx(Component_FindingResults, { findingResults: result, sectionConfig: section.config, hideIfNormal: this.state.onlyAbnormalResults }, result.variable._id)) })] });
    };
    renderFindings_ByOrder = () => {
        const section = this.state.section;
        const groups = ModuleFE_Advisor_FindingView.getFindingsForSection.byOrder(section);
        if (!groups.length && this.state.hideIfEmpty)
            return;
        return _jsxs(LL_V_L, { className: this.getSectionClassName(), children: [!this.state.hideSectionLabel &&
                    _jsx("div", { className: 'finding-view-section__label', children: section.label }), groups.map(group => _jsx(Component_FindingGroup, { result: group, sectionConfig: section.config }, group.order?._id ?? 'ungrouped'))] });
    };
    renderFindings_FishBone = () => {
        const section = this.state.section;
        const results = ModuleFE_Advisor_FindingView.getFindingsForSection.fishbone(section);
        if (!results.length && this.state.hideIfEmpty)
            return;
        const translatedResults = translateFishboneResults(results);
        const hasAbnormalResults = _values(translatedResults).some(results => {
            const latest = results[0];
            switch (typeof latest?.value) {
                case 'string':
                    return latest.value === valueID_Pos;
                case 'number': {
                    const result = latest;
                    return !(result.value >= result.normalMin && result.value <= result.normalMax);
                }
            }
            return false;
        });
        if (this.state.onlyAbnormalResults && !hasAbnormalResults)
            return;
        return _jsxs(LL_V_L, { className: this.getSectionClassName(), children: [!this.state.hideSectionLabel &&
                    _jsx("div", { className: 'finding-view-section__label', children: section.label }), !!section.data.fishbone &&
                    _jsx(Renderer_FishBone, { showTooltipSource: ModuleFE_AdvisorSettings.getAdvisorConfig(advisorConfig_DevMode), struct: section.data.fishbone ?? {}, results: translatedResults, modules: {
                            variable: ModuleFE_Advisor_Variable,
                            value: ModuleFE_Advisor_Value,
                            measurementUnit: ModuleFE_Advisor_MeasurementUnit,
                        }, onCellTooltipOpen: (variableId, metadata) => {
                            const view = ModuleFE_PanelFindings.getCurrentOnScreenView();
                            ModuleFE_AdvisorAnalytics.trigger.findings.findings_tooltipOpen({
                                variableId: variableId,
                                currentEncounter: metadata.isCurrentEncounter,
                                viewId: view._id,
                                viewLabel: view.label,
                            });
                        } })] });
    };
    renderFindings_Graph = () => {
        const section = this.state.section;
        const results = ModuleFE_Advisor_FindingView.getFindingsForSection.graph(section);
        if (!results.length && this.state.hideIfEmpty)
            return;
        const translatedResults = translateGraphResults(results);
        if (!results.length && !section.config.alwaysVisible)
            return;
        return _jsxs(LL_V_L, { className: this.getSectionClassName(), children: [!this.state.hideSectionLabel &&
                    _jsx("div", { className: 'finding-view-section__label', children: section.label }), _jsx(Renderer_SectionGraph, { ref: this.graphRef, results: translatedResults, variableOrder: section.data.variableIds })] });
    };
}
//# sourceMappingURL=Component_FindingViewSection.js.map